// English texts. To add a language: copy fi.ts to <code>.ts, translate the values (keep the keys) and add it to
// STRINGS in strings.ts. The compiler checks that every key is there and that every function takes the same arguments.

/** A value put into a text: a number or an already formatted string. */
type V = string | number;

export const en = {
  _name: "English",
  _locale: "en-GB",

  appName: "ReelHunt",
  appShort: "ReelHunt",
  viewLog: "Catches", viewMap: "Map", viewMore: "More",
  tabLog: "Catches", tabMap: "Map", tabAdd: "Catch", tabMore: "More",
  all: "All", fishPl: "Fish", gamePl: "Game",
  fish: "Fish", game: "Game",
  showFilter: "Show",

  // Log
  seasonFish: (_n: V, yr: V) => `fish in ${yr}`,
  seasonGame: (_n: V, yr: V) => `game in ${yr}`,
  heaviest: "heaviest",
  heaviestOf: (name: V) => `heaviest: ${name}`,
  released: "Released",
  wxGetting: "Getting weather…",
  wxOffline: "Weather added when online",
  emptyTitle: "Log your first catch",
  emptyText: "Tap “Catch” and take a photo. The place, time and weather are saved for you.",
  emptyAdd: "Add a catch",
  emptyFilteredTitle: "Nothing here yet",
  emptyFiltered: (kind: string): string => kind === "fish" ? "No fish logged yet." : "No game logged yet.",

  // Map
  mapOffline: "The map needs an internet connection the first time it opens.",
  mapLocate: "Show my location", mapFit: "Show all catches", mapLayer: "Switch map style",
  mapTerrain: "Terrain map", mapStreets: "Street map",
  mapFull: "Full screen map", mapExitFull: "Exit full screen", mapExpand: "Enlarge map", done: "Done",
  mapHide: "Hide buttons: show only the map", mapShow: "Show buttons again",
  depthTitle: "Water depths", depthDesc: "Depth areas, contour lines and numbers (Traficom, Syke)",
  mapDepth: "Show water depths",
  depthOn: "Water depths shown", depthOff: "Water depths hidden",
  you: "You",
  open: "Open",
  locationOff: "Location is off. Allow location for this site in your browser settings.",

  // Editor
  newCatch: "New catch", editCatch: "Edit catch",
  closeNoSave: "Close without saving",
  photos: "Photos", takePhoto: "Take photo", addMorePhotos: "Add more",
  removePhoto: "Remove photo", photoAlt: "Catch photo",
  photoPreparing: "Preparing photo…",
  photoTime: "Time set from the photo. Change it below if it is wrong.",
  photoTimePlace: "Time and place set from the photo. Change them below if they are wrong.",
  photoBad: "That photo could not be read. Try another one.",
  speciesLbl: "Species", speciesFishPh: "e.g. Pike", speciesGamePh: "e.g. Moose",
  weight: "Weight", length: "Length", when: "When", method: "Method",
  gearFish: "Lure or bait", gearGame: "Weapon and ammo",
  gearFishPh: "e.g. Rapala Shad Rap 7 cm, firetiger", gearGamePh: "e.g. Sako 85, .308 Win",
  releasedLong: "Released back to the water",
  place: "Place",
  locFinding: "Finding your location…",
  locRough: (d: V) => `Your location is rough (±${d}). Check that ReelHunt may use your precise location in the phone's settings (Apps → ReelHunt → Location → Use precise location).`,
  locUse: "Use my location",
  locNone: "No spot set. Tap the map to mark where it happened.",
  locGpsAcc: (m: V) => `Your location (±${m} m)`,
  locGps: "Your location", locPhoto: "From the photo", locMap: "Set on the map", locSaved: "Saved spot",
  locOff: "Location is off. Tap the map to mark the spot, or allow location for this site.",
  miniMapOffline: "Map unavailable offline. Your GPS position is still saved.",
  placeFishPh: "Name the spot, e.g. Pike bay, north shore", placeGamePh: "Name the spot, e.g. Stand 7, edge of the bog",
  notes: "Notes", notesFishPh: "Depth, water temperature, what worked, who was there", notesGamePh: "Wind, where the game came from, the dog, who was there",
  wxAuto: "Weather for this place and time is saved automatically.",
  cancel: "Cancel", saveCatch: "Save catch", saving: "Saving…",
  needSpecies: "Add the species first",
  saved: "Catch saved", changesSaved: "Changes saved",
  storageFull: "Phone storage is full. Free some space and try again.",
  saveFailed: "Could not save. Try again.",
  saveStuck: "Saving did not finish. Close the app completely, open it again and save once more.",
  speciesShowList: "Show the species list", speciesRecent: "Recent", speciesAll: "All species",
  speciesNoMatch: "Not in the list: it's saved as you typed it.",

  // Detail
  close: "Close",
  wxHeading: "Weather at the time",
  wxNoSpot: "No spot was set, so no weather was saved. Edit the catch and mark the spot to add it.",
  wxPendingLong: "Getting the weather…",
  wxOfflineLong: "Weather will be added when you are back online.",
  weather: "Weather", wind: "Wind", gusts: (v: V) => `gusts ${v}`,
  pressure: "Air pressure", cloud: "Cloud cover", rain: "Rain", humidity: "Humidity",
  moon: "Moon", moonLit: (p: V) => `${p} % lit`,
  trend: { falling: "falling", rising: "rising", steady: "steady" },
  trend3h: (word: V, d: V) => `${word} (${d} in 3 h)`,
  spot: "Spot", directions: "Directions to this spot",
  omaRiistaOpen: "Open Oma riista", omaRiistaHint: "Harvest reports are made in Oma riista. If the app isn't installed, this opens its store page.",
  edit: "Edit", del: "Delete", delConfirm: "Tap again to delete",
  deleted: "Catch deleted", deleteFailed: "Could not delete. Try again.",

  // More
  langTitle: "Language",
  backupTitle: "Back up your log",
  backupText: "Your catches and photos are stored only on this phone. Save a backup file now and then, and use it to move your log to a new phone.",
  backupSave: "Save backup file", backupRestore: "Restore from backup",
  backupPreparing: "Preparing backup…",
  backupShared: (n: V, p: V = 0) => `Backup ready: ${n} catches, ${p} pins.`,
  backupSaved: (name: V, n: V, p: V = 0) => `Saved ${name}: ${n} catches, ${p} pins.`,
  backupFailed: "Could not make the backup. Try again.",
  backupShareTitle: "ReelHunt backup",
  restoring: "Restoring…",
  restored: (n: V) => `Restored ${n} catches. Catches already on this phone were kept.`,
  restoreBad: "That file is not a ReelHunt backup.",
  homeTitle: "Put it on your home screen",
  homeText: "On iPhone: tap Share, then “Add to Home Screen”. On Android: open the browser menu and tap “Add to Home screen” or “Install app”. It then opens full screen like a normal app.",
  aboutTitle: "About the data",
  aboutText: "Weather is looked up from Open-Meteo for the place and hour of each catch. If you have no signal, the catch is saved and the weather is added when you are back online. Maps © OpenStreetMap contributors.",
  depthAbout: "Water depths: nautical chart data 10/2026 from the Finnish Transport and Communications Agency (Traficom), used under Traficom’s hydrographic data license, and lake soundings from the Finnish Environment Institute (Syke, CC BY 4.0). The depth map is for planning fishing trips, not for navigation. Depths can be out of date.",
  storageInfo: (n: V, ph: V, mb?: V | null) => `${n} catches and ${ph} photos stored` + (mb ? `, using about ${mb} MB.` : "."),
  storageBlocked: "Storage is blocked in this browser, so catches can't be saved.",

  // Water temperature
  waterTemp: "Water temperature",
  waterPh: "Type your own reading, or leave empty and it is filled in from the nearest lake station or the sea model.",
  waterAutoPh: (v: V, src: V) => `Automatic: ${v} °C (${src}). Type your own reading to replace it.`,
  waterFetching: "Getting the water temperature…",
  waterNoneNear: "No water temperature measured nearby. Type your own reading if you have one.",
  waterManual: "your reading", waterSea: "sea, forecast model",
  waterStation: (name: V, km: V) => `station ${name}, ${km} km away`,
  waterChip: (v: V) => `water ${v}°C`,
  waterShort: "water", waterNowHint: (v: V, src: V) => `Water ${v} °C (${src}).`,
  mapStyles: "Map style",
  mapTerrainDesc: "Terrain and contour lines (OpenTopoMap)",
  mapMmlDesc: "Most detailed map of Finland; can be saved for offline use (National Land Survey)",
  mapStreetsDesc: "Roads and place names (OpenStreetMap)",
  // Map tools
  mapPin: "Add a pin", mapDownload: "Save area for offline use", mapMml: "Finnish topographic map",
  placeHint: "Move the map so the cross is on the spot", placeHere: "Place pin here",
  pinNew: "New pin", pinEdit: "Edit pin", pinType: "Type", pinName: "Name", pinNamePh: "e.g. North stand, Pike bay",
  pinNotesPh: "Depth, how to get there, who uses the stand", pinPrivate: "Pins are private and stay on this phone.",
  pinSave: "Save pin", pinSaved: "Pin saved", pinDeleted: "Pin deleted", directionsShort: "Directions",
  pinTypes: { spot: "Fishing spot", stand: "Hunting stand", boat: "Boat launch", camera: "Trail camera", parking: "Parking", other: "Other" },
  route: "Route", routeLen: (km: V) => `${km} km`, routeDeleted: "Route deleted", gpxSave: "Save as GPX",
  windNow: "Wind now", windHint: "The arrow shows where the wind blows. Your scent drifts the same way.",
  offlineTitle: "Offline maps",
  offlineText: "Save a map area for places without signal: open the map, show the area you need and tap the download button. The Finnish topographic map and water depths are saved.",
  offlineNeedKey: "Offline maps use the National Land Survey of Finland's topographic map, which needs a free API key. It isn't set up yet.",
  offlineUnsupported: "This browser can't save maps for offline use.",
  offlineTooBig: "This area is too big to save. Zoom in a little and try again.",
  offlineAsk: (n: V, mb: V) => `Save the area you see for use without signal? Up to about ${mb} MB (${n} map tiles).`,
  download: "Download", ok: "OK",
  offlineProgress: (a: V, b: V) => `Saving map… ${a} / ${b}`,
  offlineDone: "Area saved. It now works without signal.",
  offlineFailed: (n: V) => `${n} map tiles could not be saved. Try again with a better connection.`,
  offlineInfo: (n: V, mb: V) => `${n} map tiles saved, up to about ${mb} MB.`,
  offlineNone: "No map areas saved yet.",
  offlineClear: "Delete saved maps", offlineCleared: "Saved maps deleted",
  placesTitle: "Routes and places (GPX)",
  placesText: "Import routes, waypoints and stands as GPX, KML or KMZ files from Garmin, onX, HuntStand, Google Earth or other GPS apps (export them from the other app first, often on its website). Stands, trail cameras and parking are recognised from their names and symbols. Export your pins, routes and catch spots to use them on a Garmin device or in another app.",
  placesInfo: (p: V, r: V) => `${p} pins and ${r} routes on this phone.`,
  gpxImport: "Import GPX / KML", gpxExport: "Export GPX", gpxTitle: "Routes and places",
  gpxImported: (p: V, r: V) => `Imported ${p} waypoints and ${r} routes.`,
  gpxSomeBad: (n: V) => n === 1 ? "One file could not be read." : `${n} files could not be read.`,
  gpxBad: "No routes or places found. Choose a GPX, KML or KMZ file.",
  gpxExported: (p: V, r: V, c: V) => `Exported ${p} pins, ${r} routes and ${c} catch spots.`,
  gpxNothing: "Nothing to export yet. Add pins, import routes or log catches first.",
  gpxFailed: "Could not make the GPX file. Try again.",

  // Groups
  viewGroups: "Groups", tabGroups: "Groups",
  groupsIntro: "Private groups for your fishing friends and hunting party. Messages, photos and spots are encrypted on your phone; the server only passes on scrambled data and can't read it.",
  groupYou: "You appear as", groupNoName: "(no name yet)", change: "Change",
  groupNew: "New group", groupJoinLink: "Join with a link", groupInvite: "Invite",
  groupsEmptyTitle: "No groups yet", groupsEmptyText: "Create a group and send the invite link to a friend, or open an invite link you got. A friend is simply a group of two.",
  groupNoMessages: "No messages yet", groupBroken: "This group's link no longer works", groupUnnamed: "Group", groupSomeone: "Someone",
  groupName: "Group name", groupNamePh: "e.g. Pike crew, Hunting party", groupNeedGroupName: "Give the group a name",
  groupYourName: "Your name in groups", groupYourNamePh: "e.g. Paul", groupNeedName: "Add the name others will see",
  groupNameHint: "This name is shown with your messages in all groups.",
  groupNewHint: "Next you'll get an invite link to send to the others.", groupCreate: "Create group",
  groupOffline: "No connection to the server. Check your internet and try again.",
  groupInviteTitle: (n: V) => `Invite to ${n}`,
  groupInviteText: "Send this link to the people you want in the group, or let them scan the code with their phone camera.",
  groupInviteWarn: "Anyone with this link can join and read the group, so only share it with people you trust.",
  groupInviteShareTitle: (n: V) => `Join ${n}`, groupInviteShareText: (n: V) => `Join my group ${n} in ReelHunt:`,
  copyLink: "Copy link", copied: "Copied", shareLink: "Share link",
  groupJoinTitle: "Join a group", groupJoinText: "You've been invited. Your name is shown to the group with your messages.", groupJoin: "Join",
  groupPasteLbl: "Invite link", groupPastePh: "Paste the link you got", groupBadLink: "That isn't a group invite link.", next: "Next",
  groupChangeName: "Your name", save: "Save",
  groupMembersSeen: (n: V) => n === 1 ? "1 member so far" : `${n} members`,
  groupLeave: "Leave group", groupLeaveConfirm: "Tap again to leave", groupLeft: "You left the group. Its messages were removed from this phone.",
  chatEmpty: "No messages yet. Say hello, or share a catch.", chatPh: "Message", chatSend: "Send",
  chatPrivacy: "Encrypted · kept on the server for up to 30 days",
  msgPhoto: "Photo", msgCatch: (s: V) => `Catch: ${s}`, msgPin: (n: V) => `Spot: ${n}`, msgJoined: (n: V) => `${n} joined`, msgYou: "You",
  msgSpotHidden: "Spot not shared", msgShowMap: "Show on map", msgSavePin: "Save to my map", msgSaved: "Saved",
  msgNotSent: "Not sent. Check your connection and try again.", msgTooBig: "That's too big to send. Try a smaller photo.",
  share: "Share", shareTo: "Send to", shareChoose: "Choose a group first", shareSend: "Send", shareSent: (n: V) => `Sent to ${n}`,
  shareNoGroups: "Create or join a group first (Groups tab).",
  shareCatchTitle: "Share catch", shareSpot: "Include the exact spot",
  shareSpotHint: "Off by default, so your spot stays secret. The photo, size, time and weather are always sent.",
  sharePinTitle: "Share spot", sharePinHint: "The group sees the pin's name, type, notes and exact location.",

  // Profile and tips
  profileTitle: "Your fishing and hunting",
  profileMoreText: "Tell the app how you usually fish and hunt, and it suggests good days and nights from the weather forecast.",
  profileIntro: "Choose everything that fits. The tips on the Catches tab are based on this and the next two days' forecast.",
  profileEdit: "Edit", profileSaved: "Saved. Tips are updated.", profileEmpty: "Nothing chosen yet.",
  profileFishLine: (x: V) => `Fishing: ${x}`, profileHuntLine: (x: V) => `Hunting: ${x}`,
  pfFishHow: "How do you fish?", pfFishWhat: "What do you fish for?", pfHuntHow: "How do you hunt?", pfHuntWhat: "What do you hunt?",
  huntStyles: { stand: "Stand hunting", dog: "With a dog", stalk: "Stalking", drive: "Driven hunt", waterfowl: "Waterfowl", bow: "Bow hunting", trapping: "Trapping" },
  tipsHome: "Area for the forecast", tipsHomeSet: (a: V, b: V) => `Your chosen area (${a}, ${b}).`,
  tipsHomeNone: "Not set: the place of your latest catch is used.", tipsHomeClear: "Use latest catch instead",
  tipsTitle: "Tips for the next 2 days", tipsEdit: "Change",
  tipsSetupTitle: "Get tips for good fishing and hunting weather",
  tipsSetupText: "Tell the app how you fish and hunt, e.g. nets or stand hunting.", tipsSetupBtn: "Set up",
  tipsNoPlace: "Choose an area for the forecast, or log a catch with a spot.", tipsSetPlace: "Choose area",
  tipsLoading: "Getting the forecast…", tipsOffline: "Tips need an internet connection.",
  tipsNone: "No special conditions in the next two days for the way you fish and hunt.",
  tipsForHome: "Forecast for your chosen area (Open-Meteo).", tipsForCatch: "Forecast for the place of your latest catch (Open-Meteo).",
  when_night_0: "tonight", when_night_1: "tomorrow night",
  when_evening_0: "this evening", when_evening_1: "tomorrow evening",
  when_morning_0: "this morning", when_morning_1: "tomorrow morning", when_morning_2: "the morning after tomorrow",
  when_day_0: "today", when_day_1: "tomorrow",
  tipNetCalmTitle: (w: V) => `Calm ${w}: good for nets`,
  tipNetCalmText: (w: V, g: V) => `Wind at most ${w} m/s overnight (gusts ${g}). A good night to set nets or long-lines.`,
  tipNetWindTitle: (w: V) => `Strong wind ${w}`,
  tipNetWindText: (w: V, g: V) => `Wind up to ${w} m/s, gusts ${g}. Nets may drift or tangle; consider lifting them.`,
  tipFlyTitle: (w: V) => `Calm ${w} for fly fishing`,
  tipFlyText: (w: V) => `Wind at most ${w} m/s and no rain. Good for casting and for seeing rises.`,
  tipPressureTitle: "Air pressure is falling",
  tipPressureText: (d: V, when: V) => `Down ${d} hPa in 6 hours from ${when}. Many anglers find predatory fish bite actively when the pressure drops.`,
  tipStandTitle: (w: V, dir: V) => `Steady ${dir} wind ${w}`,
  tipStandText: (from: V, to: V, w: V) => `The wind blows ${from} (up to ${w} m/s), so your scent drifts ${to}. Choose a stand where game comes from upwind.`,
  tipDogTitle: (w: V) => `Good dog hunting weather ${w}`,
  tipDogText: (w: V) => `Dry, with wind at most ${w} m/s. Good conditions for the dog to work and for hearing it bark.`,
  tipSnowTitle: (w: V) => `Fresh snow for tracking ${w}`,
  tipSnowText: (cm: V) => `About ${cm} cm of new snow overnight, then it stops. Fresh tracks are easy to read.`,

  // Notifications
  pushTitle: "Notifications",
  pushMsgs: "New group messages", pushMsgsText: "Who wrote what, decrypted on your phone.",
  pushMsgsTextNative: "A notice when your group has new messages (they stay encrypted: open the group to read them).",
  pushTips: "Evening weather tips", pushTipsText: "Around 18:00, only when there's a tip for how you fish and hunt.",
  pushPrivacy: "Group notifications contain no message text; your phone fetches and decrypts the message itself. For weather tips the server keeps your rough area (about 10 km) and your fishing and hunting choices, with no name.",
  pushMsgsOn: "Notifications on for group messages", pushTipsOn: "Evening weather tips on",
  pushNeedHome: "On iPhone, notifications work once ReelHunt is added to the home screen (Share → Add to Home Screen). Open it from there.",
  pushUnsupported: "This browser can't show notifications.",
  pushBlocked: "Notifications are blocked for ReelHunt. Allow them in your phone's settings for this site or app.",
  pushDenied: "Notifications were not allowed.", pushNeedProfile: "First tell the app how you fish and hunt.",
  pushFailed: "That didn't work. Check your connection and try again.",
  // Learning from your catches
  learnTitle: "Your best conditions",
  learnSpecies: (name: V, n: V) => `${name} · ${n} catches`,
  learnTime: (a: V, b: V) => `${a}–${b}`, learnWind: (w: V) => `wind ≤ ${w} m/s`,
  learnTrend_falling: "pressure falling", learnTrend_rising: "pressure rising", learnTrend_steady: "steady pressure",
  learnTimeLong: (a: V, b: V, p: V) => `Most catches between ${a} and ${b} (${p} %)`,
  learnWindLong: (w: V) => `Wind usually at most ${w} m/s`,
  learnTrendLong_falling: (p: V) => `Air pressure falling (${p} % of catches)`,
  learnTrendLong_rising: (p: V) => `Air pressure rising (${p} % of catches)`,
  learnTrendLong_steady: (p: V) => `Air pressure steady (${p} % of catches)`,
  learnWater: (a: V, b: V) => `Water usually ${a}–${b} °C`, learnMoon: (m: V) => `Often around the ${m}`,
  learnFoot: "From your own catches with weather saved. When the coming weather matches, you get a tip.",
  learnNotYet: (min: V, n: V) => `After ${min} catches of the same species, ReelHunt shows the conditions you catch them in (now ${n}).`,
  tipLearnedTitle: (name: string) => `Your kind of ${name.toLowerCase()} weather`,
  tipLearnedText: (n: V, when: V, summary: V) => `From ${when} the weather matches your ${n} earlier catches: ${summary}.`,

  // Keeping your data safe
  snapTitle: "Automatic copies on this phone",
  snapText: "Every day the app keeps a copy of your pins, routes and catches (the last 7), plus one before every import or restore. Restoring brings back anything missing and never removes newer things. Photos are not in the copies.",
  snapNone: "No copies yet. The first one is made today once you have pins or catches.",
  snapRestore: "Restore", snapConfirm: "Tap again to restore",
  snapCounts: (p: V, r: V, c: V) => `${p} ${p === 1 ? "pin" : "pins"} · ${r} ${r === 1 ? "route" : "routes"} · ${c} ${c === 1 ? "catch" : "catches"}`,
  snapReason_import: "before an import", snapReason_restore: "before a restore",
  snapRestored: (n: V) => `Brought back ${n} items.`, snapNothing: "Nothing was missing; everything in that copy is still here.",
  agoToday: "today", agoYesterday: "yesterday", agoDays: (n: V) => `${n} days ago`,
  backupLast: (a: V) => `Last backup file: ${a}.`, backupNever: "No backup file made yet.",
  storageProtected: "This phone keeps the app's data safe from automatic clearing.",
  storageNotProtected: "The browser may clear the app's data if the phone runs low on space. Add ReelHunt to the home screen and make backup files now and then.",
  nudgeTitle: "Back up your stands and catches",
  nudgeText: (a: V, n: V) => `Last backup file ${a}. You have ${n} pins and catches only on this phone.`,
  nudgeTextNever: (n: V) => `You have ${n} pins and catches only on this phone. One tap saves a backup file.`,
  nudgeNow: "Back up now", nudgeLater: "Later",
  gpxDuplicates: (n: V) => n === 1 ? "1 was already here and was skipped." : `${n} were already here and were skipped.`,
  gpxAllDuplicates: (n: V) => `Everything in the file (${n}) is already here.`,
  windSource: (age: V) => `Open-Meteo · ${age}`, windJustNow: "just updated", windMinutes: (n: V) => `${n} min ago`,
  windSourceLong: (time: V) => `Wind from Open-Meteo, updated ${time}; it refreshes every 10 minutes while the map is open.`,

  // Club stand board
  clubShort: "Stands", clubOpen: "Club stand board", clubTitle: (n: V) => `Stands · ${n}`,
  clubSummary: (taken: V, all: V) => `${taken} of ${all} stands taken`,
  clubEmpty: "No club stands yet. Add your own stand pins, or put a stand at the middle of the map.",
  clubFree: "Free", clubMine: "Your stand", clubTakenBy: (n: V) => `Taken by ${n}`,
  clubReserve: "Reserve", clubCancel: "Cancel", clubFreeIt: "Free it", clubConfirm: "Tap again", clubStand: "Stand",
  clubRemove: (n: V) => `Remove ${n} from the club map`,
  clubLost: (n: V) => `${n} reserved that stand just before you.`,
  clubAddStands: "Add stands", clubFromPins: "Your own stand pins", clubAddChosen: "Add chosen",
  clubNoOwnStands: "You have no stand pins of your own yet.", clubAtCenter: "New stand at the cross on the map",
  clubNamePh: "Name, e.g. Stand 7", clubAdd: "Add",
  clubPrivacy: "The board is shared only with this group and encrypted like messages. If two people reserve the same stand at once, the first one wins.",
  drawBtn: "Stand draw", drawWho: (n: V) => `Who is hunting? They are shuffled onto the ${n} free stands.`, drawGo: "Draw stands",
  drawNothing: "No free stands or no hunters left for this day.", drawTooFew: (n: V) => `Only ${n} stands were free; the rest got no stand.`, drawDone: "Stands drawn.",
  evStandAdded: (who: V, s: V) => `${who} added stand ${s}`, evStandRemoved: (who: V) => `${who} removed a stand`,
  evAreaSet: (who: V) => `${who} set the club area from Oma riista`, evAreaRemoved: (who: V) => `${who} removed the club area`,
  areaAdd: "Show the club area from Oma riista", areaTitle: "Club area", areaChange: "Change", areaLoading: "Loading the club area from Oma riista…",
  areaSignIn: "Sign in to see the club area from Oma riista (Oma riista needs to know who asked).",
  areaNotFound: (id: V) => `Oma riista has no area with the ID ${id}.`, areaOffline: "No connection: the club area can’t be loaded now.",
  areaUnavailable: "Oma riista can’t be reached right now. Try again later.", areaYear: (a: V, b: V) => `hunting year ${a}–${b}`,
  areaImport: (n: V) => `Add stands from Oma riista (${n})`, areaImported: (n: V) => `${n} stands added from Oma riista`,
  areaIdLbl: "Oma riista area ID", areaSave: "Show", areaRemove: "Remove the club area", areaBadId: "The area ID is at least 10 letters and digits.",
  areaIdHint: "You find it in Oma riista in the club’s hunting area details (Alueen tunnus / area ID). Everyone in the group then sees the area and its stands on this map.",
  poiStand: "Stand", poiFeeding: "Feeding place", poiLick: "Mineral lick", poiOther: "Place",
  evReserved: (who: V, s: V, d: V) => `${who} reserved ${s} for ${d}`, evDrawn: (who: V, s: V, d: V) => `Stand draw: ${who} got ${s} for ${d}`,
  evCancelled: (who: V, s: V, d: V) => `${s} is free again for ${d} (${who})`,

  // Photo feed
  chatTabChat: "Chat", chatTabFeed: "Photos",
  feedNew: "New photo", feedEmpty: "No photos yet. Post a photo, or share a catch: it shows up here as a card the group can like and comment on.",
  feedOpen: "Open photo", feedComment: "Comment", feedLike: "Like", feedUnlike: "Remove like",
  feedAllComments: (n: V) => `See all ${n} comments`, feedLikedBy: (names: V) => `Liked by ${names}`,
  feedComments: (n: V) => n === 1 ? "1 comment" : `${n} comments`, feedNoComments: "No comments yet",
  feedCommentPh: "Write a comment", feedCaption: "Caption", feedCaptionPh: "What's happening in the photo?",
  msgComment: (text: V) => `commented: ${text}`,

  // Season presentation
  seasonBtn: "Season recap", seasonOwnBtn: "My season", seasonOwnTitle: "My season",
  seasonPick: "Season recap", seasonPickText: "Choose the season. The recap plays full screen: tap right to go on, left to go back, hold to pause. To watch together, show it on a TV with screen mirroring.",
  seasonLast12: "Last 12 months", seasonEmpty: "Nothing to show yet: post photos or share catches first.",
  seasonKicker: "Season recap", seasonOf: (y: V) => `Season ${y}`,
  seasonNumbers: "The season in numbers", seasonCatches: "catches", seasonSpecies: "species", seasonWeight: "of fish in total", seasonPhotos: "photos", seasonPeople: "hunters and anglers",
  seasonBiggest: "Biggest fish", seasonBiggestGame: "Biggest game", seasonTopSpecies: "Most caught", seasonMostLiked: "Most liked photo", seasonLikes: (n: V) => n === 1 ? "1 like" : `${n} likes`,
  seasonMonths: "Catches by month", seasonBestMonth: (m: V, n: V) => `Best month: ${m} (${n})`,
  seasonPeopleTitle: "Who caught what", seasonThanks: "Thanks for the season!", seasonEndLine: (c: V, p: V) => `${c} catches · ${p} photos. See you next season.`,

  themeTitle: "Theme", theme_auto: "Automatic", theme_light: "Light", theme_dark: "Dark",
  privacyLink: "Privacy policy",
  // Feedback
  feedbackTitle: "Feedback",
  feedbackPanel: "Something not working, or an idea? Tell us: every message is read.",
  feedbackOpen: "Send feedback",
  guideLink: "Getting started",
  feedbackIntro: "What happened, or what would make ReelHunt better for you?",
  feedbackText: "Your message",
  feedbackTextPh: "e.g. the map froze when I…",
  feedbackEmail: "Email for a reply (optional)",
  feedbackSent: (v: V) => `The app version (${v}), device type, language and screen size are sent with it. No places, catches or group data.`,
  feedbackSend: "Send",
  feedbackThanks: "Thank you! Your feedback was sent.",
  feedbackFailed: "Couldn't send. Check your connection and try again.",
  appVersion: (v: V) => `Version ${v}`,

  // Trip mode
  tripStart: "Start a trip",
  tripWhat: "What are you after?",
  tripAnything: "Anything",
  tripSpeciesPh: "Species (optional)",
  tripRouteNote: "The route is recorded while ReelHunt is open on screen. On a long trip, turn on “Keep screen on”.",
  tripRouteNoteNative: "The route is recorded even with the screen off. A notification shows while the trip is on; end the trip to stop it.",
  tripRecordingTitle: "ReelHunt is recording your trip",
  tripRecordingText: "Your route is saved while the trip is on. End the trip in the app to stop.",
  tripNoLocation: "ReelHunt can't use your location. Allow it in the phone's settings (Apps → ReelHunt → Permissions).",
  pushNativeSoon: "Notifications are coming to the Android app soon. Meanwhile they work in the web app (reelhunt.fi/app in Chrome).",
  tripGo: "Start",
  tripStarted: "Trip started. Good luck!",
  tripEnd: "End",
  tripEndConfirm: "End trip?",
  tripSpots: "Spots",
  tripScreenOn: "Keep screen on",
  tripCatches: (n: V) => n === 1 ? "1 catch" : `${n} catches`,
  durHm: (h: V, m: V) => `${h} h ${m} min`,
  durM: (m: V) => `${m} min`,
  spotsTitle: "Suggested spots",
  spotsFor: (name: V) => `For ${name}, from your earlier catches and today's weather.`,
  spotsLoading: "Finding spots…",
  spotsNone: (name: V) => `No earlier catches of ${name} with a place yet. Log your catches on this trip, and next time you get suggestions here.`,
  spotsOffline: "No weather right now: the suggestions use only the time of year and day.",
  spotMatch: (p: V) => `${p} % match`,
  spotCatches: (n: V, when: V) => `${n === 1 ? "1 catch" : `${n} catches`} here, latest ${when}`,
  spotShared: (who: V) => `incl. shared by ${who}`,
  spotKm: (km: V) => `${km} km away`,
  spotShow: "Show",
  reason_season: "same time of year", reason_hour: "same time of day", reason_wind: "similar wind",
  reason_dir: "same wind direction", reason_trend: "same pressure trend", reason_water: "similar water temperature",
  tripSummary: "Trip summary",
  tripTime: "Time",
  tripDistance: "Distance",
  tripCaught: "Catches",
  tripNoCatches: "No catches this time.",
  tripWeatherStart: "Weather at start",
  tripSaveGpx: "Save route (GPX)",
  tripShowMap: "Show on map",
  tripDelete: "Delete trip",
  tripDeleted: "Trip deleted.",
  tripsTitle: "Your trips",
  tripsNone: "No trips yet. Start one from the map.",

  // Account and cloud backup
  accountTitle: "Account and cloud backup",
  accountIntro: "Optional. Sign in with your email to back up your catches, pins and groups to the cloud and get them back on a new phone. No password: we email you a code.",
  accountSignIn: "Sign in",
  accountAs: (email: V) => `Signed in as ${email}`,
  accountNews: "News by email",
  accountNewsText: "Now and then, about new features. You can turn it off any time.",
  accountSignOut: "Sign out",
  accountDelete: "Delete account",
  accountDeleteConfirm: "Tap again to delete",
  accountDeleted: "Account and cloud backup deleted. Everything on this phone stays.",
  signedOut: "Signed out. Everything on this phone stays.",
  signInTitle: "Sign in",
  signInEmail: "Email",
  signInEmailPh: "you@example.com",
  signInNews: "You may email me news about ReelHunt (optional)",
  signInSendCode: "Send code",
  signInCodeSent: (email: V) => `We sent a 6-digit code to ${email}. It can take a minute; check the spam folder too.`,
  signInCode: "Code",
  signInResend: "Send a new code",
  signInWait: (s: V) => `You can ask for a new code in ${s} s.`,
  signInBadEmail: "Check the email address.",
  signInWrongCode: (n: V) => `Wrong code. Tries left: ${n}.`,
  signInExpired: "The code has expired or was already used. Ask for a new one.",
  signInTooMany: "Too many tries. Wait a while and try again.",
  signInUnavailable: "Sign-in isn't available yet. Try again later.",
  signInFailed: "Couldn't sign in. Check your connection and try again.",
  signedIn: "Signed in.",
  cloudLast: (when: V, c: V, p: V) => `Last cloud backup ${when}: ${c} catches, ${p} pins.`,
  cloudNone: "No cloud backup yet.",
  cloudAuto: "Backs up by itself about once a day when something has changed. Photos are kept as small previews; save a backup file for full-size photos.",
  cloudNow: "Back up now",
  cloudRestore: "Restore from cloud",
  cloudRestoreConfirm: "Tap again to restore",
  cloudBackingUp: "Backing up…",
  cloudDone: "Backed up to the cloud.",
  cloudFailed: "Cloud backup failed. Check your connection.",
  cloudRestored: (n: V) => `Restored ${n} catches from the cloud. Everything already on this phone was kept.`,
  cloudRestoreFailed: "Couldn't restore. Check your connection.",
  cloudOfferTitle: "You have a cloud backup",
  cloudOffer: (c: V, p: V) => `It has ${c} catches and ${p} pins. Restore them to this phone?`,
  cloudOfferYes: "Restore",

  usageTitle: "Anonymous usage statistics",
  usageText: "Once a day the app tells us it is in use, with a few counts (such as how many catches and groups). Nothing about you, no places, nothing from your groups. It helps us see how many use ReelHunt.",

  // ReelHunt Plus and the club plan (billing.ts, paywall.ts)
  plusName: "ReelHunt Plus", plusBadge: "Plus",
  payPlusLead: "Learn from your own catches: when, where and in what weather they bite.",
  payWelcomeBack: "Your catches are all still here. Plus brings back the extras:",
  payBenefitBest: "Your best conditions: the time of day, wind and air pressure you catch each species in",
  payBenefitTip: "A tip when the coming weather matches your best catches, also as an evening notification",
  payBenefitSpots: "Suggested spots on trips, from your own catches and those your friends share",
  payBenefitSeason: "Season recap: your year as a story, and your group's",
  payBenefitPhotos: "Full-size photos in the cloud backup (coming later)",
  payFreeNote: "Logging catches, weather, the map and groups stay free. Everything you record is always yours to view and export.",
  payNoAds: "No ads. We never sell your data.",
  payYear: "Yearly", payMonth: "Monthly", unitYear: "year", unitMonth: "month",
  payAboutMonth: (p: V) => `about ${p} / month`, paySave: (n: V) => `save ${n} %`,
  payFoundingBadge: "Founding member price",
  payFounding: "Founding member price: you keep it for as long as your subscription continues.",
  payFirstFree: "First month free",
  payTrialBtn: "Try free for a month", payBuyBtn: "Subscribe",
  payThenPrice: (p: V, u: V) => `First month free, then ${p} / ${u}.`,
  payCharged: (p: V, u: V, store: V) => `${p} / ${u}, paid through ${store}.`,
  payAutoRenew: (store: V) => `The subscription renews automatically until you cancel it. Cancel any time in ${store} under Subscriptions, at least 24 hours before the period ends.`,
  payTrialCancel: "Cancelling during the free month costs nothing.",
  payRestore: "Restore purchases", payRestored: "Purchases restored.", payNothingToRestore: "No purchases found for this store account.",
  payTerms: "Terms of use",
  payLoading: "Loading prices…",
  payNoStore: "The store can't be reached right now. Check the connection and try again.",
  payBuyInApp: "Plus and the club plan are bought in the ReelHunt app (Google Play or App Store). Sign in with the same email in the app and here, and they work on the website too.",
  payPending: "The purchase isn't finished yet. If the payment is confirmed later, it turns on by itself.",
  payNotAllowed: "Purchases are blocked on this device (for example by parental controls).",
  payOffline: "No connection. Check the connection and try again.",
  payFailed: "The purchase didn't go through. Try again in a moment.",
  payRestoreFailed: "Restoring didn't work. Check the connection and that you are signed in to the same Google or Apple account.",
  payThanks: "Thank you! Plus is on.", payThanksTrial: "Your free month has started. Enjoy!",
  payWhyBest: "Your best conditions are part of ReelHunt Plus.",
  payWhySpots: "Suggested spots are part of ReelHunt Plus.",
  payWhySeason: "The season recap is part of ReelHunt Plus.",
  // club plan
  payClubTitle: "Club plan",
  payClubLead: (g: V) => `The full stand board for ${g}. One member pays, the whole group uses it.`,
  payClubB1: (n: V) => `As many stands as the club has (without the plan: ${n})`,
  payClubB2: "Stand draw for each hunting day",
  payClubB3: "For every member of the group, also those who join later",
  payClubNote: "Stands and reservations you already have always stay visible.",
  payClubBtn: "Buy for the club",
  payClubCharged: (p: V, store: V) => `${p} / year, paid by you through ${store}. Covers one group.`,
  payClubThanks: (g: V) => `The stand board is unlocked for ${g}.`,
  payClubAlready: "This group's stand board is already unlocked by another member.",
  payClubMove: "Use my club plan here",
  payClubMoveNote: "Your club plan covers one group. Moving it here locks the board's extras in the other group (its stands stay).",
  payClubClaimFailed: "Couldn't connect the club plan to this group. Check the connection and try again.",
  payWhyStands: (n: V) => `Without the club plan the board holds ${n} stands.`,
  payWhyDraw: "The stand draw is part of the club plan.",
  clubPlanOn: "Club plan on", clubFreeCount: (n: V, max: V) => `${n} / ${max} stands without the club plan`,
  clubUnlock: "Get the club plan",
  // teasers
  learnPlusText: (names: V) => `ReelHunt has learned from your catches (${names}) when and in what weather you catch them. See it with Plus.`,
  spotsPlusText: "With Plus, the trip suggests spots from your past catches and your friends' shared catches that match today's weather.",
  plusTry: "Try free", plusSee: "See Plus",
  // the Plus panel under More
  plusOff: "Plus features are free for everyone while ReelHunt is being tested.",
  plusNone: "You use the free version. Plus adds your best conditions, matching weather tips, suggested spots and the season recap.",
  plusTrial: (d: V) => `Free month until ${d}.`,
  plusTrialThen: "After that it continues as a paid subscription, unless you cancel.",
  plusActive: (plan: V) => `Plus is on · ${plan}`,
  plusRenews: (d: V) => `Renews on ${d}.`, plusEnds: (d: V) => `Ends on ${d}; it won't renew.`,
  plusExpired: (d: V) => `Plus ended on ${d}. Everything you recorded is still here.`,
  plusBillingIssue: "The store couldn't take the payment. Check your payment method in the store.",
  plusManage: "Manage subscription",
  plusClubOn: (d: V) => `Club plan on until ${d}. It covers the group you bought it for.`,
  plusClubEnded: (d: V) => `Club plan ended on ${d}.`,
  plusTest: "Test mode (simulated store)", plusTestOffer: "Founding offer", plusTestClub: "Own club plan",
  plusTest_none: "Free", plusTest_trial: "Free month", plusTest_active: "Subscribed", plusTest_expired: "Trial ended", plusTest_lapsed: "Ended", plusTest_restore: "Bought, not restored",
  // Sheltered from the wind (trip/shelter-ui.ts)
  shelterBtn: "Sheltered spots for the wind", shelterTitle: "Sheltered from the wind",
  shelterNight: "Net for the night", shelterNow: "Fishing now",
  shelterLoading: "Getting your location and the forecast…", shelterLoadingShores: "Finding the shores around you…",
  shelterOffline: "This needs an internet connection.", shelterFailed: "The forecast or map data didn't load. Try again in a moment.",
  shelterNoForecast: "The wind forecast didn't load. Check the connection and try again.",
  shelterNoMap: "The shore map didn't load. Check the connection and try again.",
  shelterWind: (from: V, s: V) => `Wind ${from}, ${s} m/s`,
  shelterWindThen: (time: V, from: V, s: V) => `at ${time} ${from}, ${s} m/s`,
  shelterGusts: (g: V) => `Gusts up to ${g} m/s.`,
  shelterGustWarn: (g: V) => `Gusts up to ${g} m/s: even sheltered nets can suffer. Consider lifting them.`,
  shelterCalm: "Light wind: almost any spot will do.",
  shelterNone: (km: V) => `No shores within ${km} km. Move nearer to water, or choose a longer distance.`,
  shelterRange: "Distance",
  shelterHistory: (n: V) => n === 1 ? "1 catch here before" : `${n} catches here before`,
  shelterHistoryShared: (n: V, f: V) => `${n} catches here before (${f} from friends)`, shelterClear: "Remove from map", shelterKmOpt: (km: V) => `${km} km`,
  shelterPfLabel: "Sheltered spots: how far to look", shelterPfHint: "2 km from the shore, 10 km by boat. You can also change it in the search.",
  shelterLevel_good: "Sheltered", shelterLevel_ok: "Fairly sheltered", shelterLevel_rough: "Windy",
  shelterFetch: (d: V) => `${d} of open water upwind`, shelterFetchNone: "Right in the lee of the shore",
  shelterWorst: (time: V, s: V, from: V) => `Windiest at ${time}: ${s} m/s ${from}`,
  shelterInWind: (from: V, level: V) => `${from}: ${level}`,
  shelterNote: "Shores © OpenStreetMap contributors (OpenFreeMap). Check depth, rules, permits and the shore owner's rights yourself.",
  shelterPlusText: "With Plus, ReelHunt finds the shores around you where the coming wind can't build waves: for a net overnight or for fishing now.",
  payWhyShelter: "Sheltered spots are part of ReelHunt Plus.",
  payBenefitShelter: "Sheltered spots for a net overnight or fishing now, by the coming wind",
  // Lists
  compass: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
  windNames: ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"],
  dirFrom: ["from the north", "from the northeast", "from the east", "from the southeast", "from the south", "from the southwest", "from the west", "from the northwest"],
  dirTo: ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"],
  moonPhases: ["New moon", "Waxing crescent", "First quarter", "Waxing gibbous", "Full moon", "Waning gibbous", "Last quarter", "Waning crescent"],
  wmo: { 0: "Clear", 1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Icy fog",
    51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle", 56: "Freezing drizzle", 57: "Freezing drizzle",
    61: "Light rain", 63: "Rain", 65: "Heavy rain", 66: "Freezing rain", 67: "Freezing rain",
    71: "Light snow", 73: "Snow", 75: "Heavy snow", 77: "Snow grains", 80: "Light showers", 81: "Showers", 82: "Heavy showers",
    85: "Snow showers", 86: "Heavy snow showers", 95: "Thunderstorm", 96: "Thunderstorm, hail", 99: "Thunderstorm, hail" },
  methods: { lure: "Lure", jig: "Jig", fly: "Fly", bait: "Bait", trolling: "Trolling", ice: "Ice fishing", net: "Net",
    rifle: "Rifle", shotgun: "Shotgun", bow: "Bow", trap: "Trap", longline: "Long-line", trapnet: "Fyke / trap net" },
  species: {
    perch: "Perch", pike: "Pike", zander: "Zander", browntrout: "Brown trout", rainbowtrout: "Rainbow trout",
    grayling: "Grayling", whitefish: "Whitefish", salmon: "Salmon", char: "Arctic char", bream: "Bream", burbot: "Burbot",
    ide: "Ide", roach: "Roach", vendace: "Vendace", asp: "Asp", tench: "Tench", seatrout: "Sea trout",
    moose: "Moose", whitetail: "White-tailed deer", roedeer: "Roe deer", fallowdeer: "Fallow deer", boar: "Wild boar",
    mountainhare: "Mountain hare", brownhare: "European hare", fox: "Red fox", raccoondog: "Raccoon dog",
    capercaillie: "Capercaillie", blackgrouse: "Black grouse", hazelgrouse: "Hazel grouse", willowgrouse: "Willow grouse",
    mallard: "Mallard", teal: "Teal", goldeneye: "Goldeneye", wigeon: "Wigeon", canadagoose: "Canada goose",
    greylag: "Greylag goose", woodpigeon: "Wood pigeon", pheasant: "Pheasant", beaver: "Beaver", woodcock: "Woodcock",
  },
};

export type Strings = typeof en;
